import { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import Modal from '@srm/ui/modal';
import DataTable from '@srm/ui/data-table';
import './AbookpickerModal.scss';
import { IAbookTab } from '@models/Forms/IForms';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import { ITab } from '@srm/ui/types/ITab';
import Tabs from '@molecules/Tabs';
import ListModeControl from './ModeControls/ListModeControl';
import TreeModeControl from './ModeControls/TreeModeControl';
import AbookExternalSearchModal from './AbookExternalSearchModal';
import { IAbookpickerRefActions } from '../IAbookpickerRefActions';
import { AddressBookService } from '@services/AddressBookService';
import { getLoadOptionsQuery } from '@utils/loadOptionsConverter';
import { IAbookSettings } from './IAbookSettings';
import ExternalSearchBarActionControl from '@controls/ExternalSearchBarActionControl';
import { portalOverlayClassName } from '@srm/ui/lib/portalOverlay';
import SelectedItemsPanel from '@srm/ui/selected-items-panel';
import { sameKeys } from '@srm/ui/lib/sameKeys';
import { useLatest } from '@srm/ui/hooks/useLatest';
import EmptyState from '@srm/ui/empty-state';
import { MdPersonSearch } from 'react-icons/md';
import { pickActiveAbookTab } from '@utils/abookTabVisibility';

export interface ISelectFromAbookDialogProp extends IAbookSettings {
    onClose: () => void;
    onSubmit: (data: IAddressBookData[]) => Promise<void>;
}

const AbookpickerModal: FC<ISelectFromAbookDialogProp> = (p: ISelectFromAbookDialogProp) => {
    const [selectedRowItems, setSelectedRowItems] = useState<IAddressBookData[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
    // Выбор, показанный после загрузки (ключи, которых нет в адресной книге, отброшены): доклады до первого действия
    // пользователя — стартовый выбор, правка — отличие от него
    const shownKeysRef = useRef<number[] | undefined>(undefined);
    const touchedRef = useRef(false);
    const touch = () => {
        touchedRef.current = true;
    };
    const onSelectedKeys = (value: number[]) => {
        if (!touchedRef.current) shownKeysRef.current = value;
        setSelectedKeys(value);
    };
    const [tabs, setTabs] = useState<ITab[]>();
    const [tabsKey, setTabsKey] = useState('');
    const [showExternalSearch, setShowExternalSearch] = useState<boolean>(false);
    const [tabSettings, setTabSettings] = useState<IAbookTab>();
    const tabSettingsRef = useLatest(tabSettings);

    const controlRef = useRef<IAbookpickerRefActions>(null);

    // Набор вкладок меняется и при открытом окне (условия visibilityRules): активная остаётся, если её не скрыли
    useEffect(() => {
        if (!p.tabsSettings) return;
        const active = pickActiveAbookTab(p.tabsSettings, tabSettingsRef.current);
        setTabSettings(active);
        if (!active) {
            setSelectedKeys([]);
            setSelectedRowItems([]);
        }
        setTabsKey(p.tabsSettings.map((item) => item.name).join('\n'));
        setTabs(
            p.tabsSettings.map((item) => ({
                label: item.name,
                active: item === active,
                handler: () => setTabSettings(item),
            })),
        );
    }, [p.tabsSettings, tabSettingsRef]);

    const noVisibleTabs = p.tabsSettings?.length === 0;

    useEffect(() => {
        if (tabSettings) {
            setSelectedKeys([]);
            setSelectedRowItems([]);
        }
    }, [tabSettings]);

    const getParams = (options: any) => {
        let params = '?';
        params += getLoadOptionsQuery(options);

        if (p.getFormValues) {
            params += '&formValues=' + encodeURIComponent(p.getFormValues());
        } else {
            params += '&formValues=' + encodeURIComponent(p.formValues);
        }

        return params;
    };

    const getSelectedKeys = async (selected: number[]) => {
        let result: number[] = [];

        await AddressBookService.fetchListSelectedKeys(tabSettings!.cache, selected).then((response) => {
            result = response.data;
        });

        return result;
    };

    const toggleShowExternalSearch = () => {
        setShowExternalSearch(!showExternalSearch);
    };

    const submittingRef = useRef(false);
    const submitSelection = async (items: IAddressBookData[]) => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        try {
            await p.onSubmit(items);
            p.onClose();
        } finally {
            submittingRef.current = false;
        }
    };
    const onConfirmRow = (row: IAddressBookData) => submitSelection([row]);

    // Подвал — элементом, не компонентом: функция-компонент, объявленная в рендере, — новый тип на каждый рендер, и
    // React монтировал подвал заново на каждый выбор строки
    const modalFooter = (
        <div className={'abookModal_footer'}>
            {p.externalSearch && tabSettings && (
                <div className="right">
                    <Button onClick={toggleShowExternalSearch} variant="light" size="sm">
                        {p.externalSearch.buttonName}
                    </Button>
                </div>
            )}
            {p.showActionsBar && p.docId && (
                <div className="right">
                    <ExternalSearchBarActionControl docId={p.docId} />
                </div>
            )}
            <Button onClick={() => submitSelection(selectedRowItems)} size="sm" disabled={noVisibleTabs}>
                Выбрать
            </Button>
            <Button onClick={p.onClose} variant="light" size="sm">
                Отменить
            </Button>
        </div>
    );

    const getControl = (mode: string) => {
        if (tabSettings) {
            switch (mode) {
                case 'tree': {
                    return (
                        <TreeModeControl
                            currentTab={tabSettings}
                            onConfirmRow={onConfirmRow}
                            onSelectedRowItems={(val: IAddressBookData[]) => {
                                setSelectedRowItems(val);
                            }}
                            onSelectedKeys={onSelectedKeys}
                            getParams={getParams}
                            selected={p.selected}
                            title={p.title}
                            isMultiple={p.isMultiple}
                            recursive={p.recursive}
                            formValues={p.formValues}
                            controlRef={controlRef}
                            wordWrapEnabled={p.wordWrapEnabled}
                        />
                    );
                }

                default:
                    return (
                        <ListModeControl
                            currentTab={tabSettings}
                            onConfirmRow={onConfirmRow}
                            onSelectedRowItems={(val: IAddressBookData[]) => {
                                setSelectedRowItems(val);
                            }}
                            onSelectedKeys={onSelectedKeys}
                            getParams={getParams}
                            getSelectedKeys={async (selected: number[]) => {
                                return await getSelectedKeys(selected);
                            }}
                            selected={p.selected}
                            title={p.title}
                            isMultiple={p.isMultiple}
                            formValues={p.formValues}
                            controlRef={controlRef}
                            wordWrapEnabled={p.wordWrapEnabled}
                        />
                    );
            }
        }
    };

    return (
        <Modal
            className={clsx('abookModal', portalOverlayClassName)}
            size="content"
            onClose={p.onClose}
            hasUnsavedInput={() => touchedRef.current && !sameKeys(selectedKeys, shownKeysRef.current ?? p.selected)}
            header={p.title}
            footer={modalFooter}
        >
            <div className="abook-container" onPointerDownCapture={touch} onKeyDownCapture={touch}>
                <div className="abook-tabs">
                    {tabs && tabs.length > 0 && (
                        // вкладки по условиям приходят и уходят: индекс активной в списке Tabs сдвинулся бы
                        <Tabs key={tabsKey} list={tabs} />
                    )}
                </div>
                <div className="abook-grid">
                    {/* Вкладки приходят с настройками поля: пока их нет, показываем
                        скелет списка — открытая пустая модалка выглядит как сбой */}
                    {tabSettings ? (
                        getControl(tabSettings.mode)
                    ) : noVisibleTabs ? (
                        <EmptyState
                            size="inline"
                            tone="neutral"
                            icon={<MdPersonSearch />}
                            title="Нет доступных вкладок"
                            description="Условия показа вкладок не выполнены. Проверьте заполнение документа."
                        />
                    ) : (
                        <DataTable columns={[]} data={[]} getRowId={() => ''} loading />
                    )}
                </div>
                <SelectedItemsPanel
                    className="selected-items-panel--picker-modal"
                    selectionMode={p.isMultiple ? 'multiple' : 'single'}
                    items={selectedRowItems}
                    getKey={(item) => item.key}
                    getLabel={(item) => item.fullName || item.name}
                    getMeta={(item) => item.department || item.department2 || item.position}
                    onRemove={(item) => controlRef.current?.unSelect(item)}
                    onClear={() => controlRef.current?.clearSelection()}
                />
            </div>
            {showExternalSearch && tabSettings && (
                <AbookExternalSearchModal
                    settings={p.externalSearch!}
                    onClose={() => {
                        toggleShowExternalSearch();
                    }}
                    onSubmit={() => {
                        controlRef.current?.reset();
                    }}
                    currentTab={tabSettings}
                    getParams={getParams}
                />
            )}
        </Modal>
    );
};

export default AbookpickerModal;
